import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import {
  getV1MeRetrieveQueryOptions,
  useSchoolChangeProposalsAssignedSchools,
  useSchoolChangeProposalsList,
} from '@/api/client'
import { SchoolProposalDiff } from '@/components/schools/school-proposal-diff'
import { PageHeader } from '@/components/page-header'
import { Pager } from '@/components/pager'
import { keepPreviousPage } from '@/lib/keep-previous-page'
import { proposalStateLabel, proposalStateTone } from '@/lib/school-proposal-state'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export const Route = createFileRoute('/_panel/moje-szkoly/')({
  // Teacher-only surface (TR-501 R10). Operators manage schools under /szkoly.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'teacher') {
      throw redirect({ to: '/' })
    }
  },
  component: MojeSzkolyPage,
})

const PAGE_SIZE = 25

function MojeSzkolyPage() {
  const navigate = useNavigate()
  const [schoolsPage, setSchoolsPage] = useState(1)
  const [proposalsPage, setProposalsPage] = useState(1)
  const schoolsParams = { page: schoolsPage, page_size: PAGE_SIZE }
  const proposalsParams = { page: proposalsPage, page_size: PAGE_SIZE }
  const schoolsQuery = useSchoolChangeProposalsAssignedSchools(schoolsParams, {
    query: { placeholderData: keepPreviousPage(schoolsParams) },
  })
  const proposalsQuery = useSchoolChangeProposalsList(proposalsParams, {
    query: { placeholderData: keepPreviousPage(proposalsParams) },
  })
  const schoolsData = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data : undefined
  const proposalsData = proposalsQuery.data?.status === 200 ? proposalsQuery.data.data : undefined
  const schools = schoolsData?.results ?? []
  const proposals = proposalsData?.results ?? []

  return (
    <>
      <PageHeader title="Moje szkoły" />
      <div className="flex flex-col gap-6 p-6">
        <Card>
          <CardHeader>
            <CardTitle>Szkoły, do których jesteś przypisany</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {schoolsQuery.isLoading && <p className="text-sm text-muted-foreground">Ładowanie…</p>}
            {!schoolsQuery.isLoading && schools.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Nie jesteś przypisany do żadnej aktywnej szkoły.
              </p>
            )}
            {schools.map((school) => (
              <div
                key={school.id}
                className="flex items-center justify-between rounded-md border px-4 py-3"
              >
                <span className="font-medium">{school.name}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: '/moje-szkoly/$szkolaId',
                      params: { szkolaId: String(school.id) },
                    })
                  }
                >
                  Zgłoś zmianę
                </Button>
              </div>
            ))}
            <Pager
              page={schoolsPage}
              count={schoolsData?.count ?? 0}
              pageSize={PAGE_SIZE}
              onPageChange={setSchoolsPage}
              className="px-0 pt-1"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Moje zgłoszenia</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {proposalsQuery.isLoading && (
              <p className="text-sm text-muted-foreground">Ładowanie…</p>
            )}
            {!proposalsQuery.isLoading && proposals.length === 0 && (
              <p className="text-sm text-muted-foreground">Nie zgłosiłeś jeszcze żadnej zmiany.</p>
            )}
            {proposals.map((proposal) => (
              <div key={proposal.id} className="flex flex-col gap-2 rounded-md border px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{proposal.school_name}</span>
                  <StatusBadge tone={proposalStateTone[proposal.state]}>
                    {proposalStateLabel[proposal.state]}
                  </StatusBadge>
                </div>
                {proposal.changes.length === 0 && proposal.baseline_source === 'live' ? (
                  <p className="text-xs text-muted-foreground">Brak podglądu zmian</p>
                ) : (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      Zmiany
                    </summary>
                    <div className="pt-2">
                      <SchoolProposalDiff proposal={proposal} />
                    </div>
                  </details>
                )}
              </div>
            ))}
            <Pager
              page={proposalsPage}
              count={proposalsData?.count ?? 0}
              pageSize={PAGE_SIZE}
              onPageChange={setProposalsPage}
              className="px-0 pt-1"
            />
          </CardContent>
        </Card>
      </div>
    </>
  )
}
